import useAuth from '@/auth/useAuth';
import { DEMO_MODE } from '@/constants';
import { supabase } from '@/lib/supabase';
import Onboard from '@/pages/onboard';
import { createBalanceInit } from '@/service/payment';
import { createUser } from '@/service/user';
import { useGetUser } from '@/service/user/hook';
import { createWorkspaceInit } from '@/service/workspace';
import { useGetWorkspaces } from '@/service/workspace/hook';
import { isNotEmpty } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar';
import { toast } from 'sonner';

type ProtectOnBoardProps = {
  children: React.ReactNode;
};
const ProtectOnBoard = ({ children }: ProtectOnBoardProps) => {
  if (DEMO_MODE) {
    return <>{children}</>;
  }

  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const loadingBarRef = useRef<LoadingBarRef>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCreateUser = async () => {
    setIsLoading(true);
    loadingBarRef.current?.continuousStart();
    try {
      if (!data) {
        await createUser({
          balance: 10,
          email: session?.user.email,
          user_id: session?.user.id,
          is_onboard: true,
          name:
            session?.user?.user_metadata?.name ||
            session?.user?.email?.split('@')[0],
        });
      }
      await createBalanceInit();
      await createWorkspaceInit();
      setIsLoading(false);
      loadingBarRef.current?.complete();
      refetch();
    } catch (err) {
      console.error(err);
      setError('Failed to create user or initialize data. Please try again.');
      setIsLoading(false);
      loadingBarRef.current?.complete();
    }
  };

  const {
    data,
    error: getUserError,
    isSuccess,
    isLoading: isUserLoading,
    isFetching,
    refetch,
  } = useGetUser({
    retry: false,
    staleTime: Infinity,
  });

  console.log('[ProtectOnBoard] useGetUser state:', {
    data: data ? 'EXISTS' : 'NULL',
    error: getUserError?.code || getUserError?.message || 'NULL',
    isSuccess,
    isUserLoading,
    isFetching,
    isLoading,
  });
  const workspaces = useGetWorkspaces({
    staleTime: Infinity,
    enabled: isNotEmpty(data),
  });

  useEffect(() => {
    if (
      getUserError?.code === 'PGRST116' ||
      (workspaces.data && workspaces.data?.length === 0)
    ) {
      handleCreateUser();
    } else if (getUserError) {
      setError('Failed to fetch user data. Please refresh the page.');
      setIsLoading(false);
      loadingBarRef.current?.complete();
    } 
    // else if (data && data?.is_active === false) {
    //   toast.error('Your account is not active. Please contact support.');
    //   supabase.auth.signOut();
    // } 
    else {
      setIsLoading(false);
      loadingBarRef.current?.complete();
    }
  }, [data, getUserError, workspaces.data]);

  const isOnBoard = data?.is_onboard;
  const ready = !isLoading && isSuccess;

  console.log('[ProtectOnBoard] ready check:', {
    isLoading,
    isSuccess,
    ready,
    isOnBoard,
    willRender: ready ? (isOnBoard ? 'Onboard' : 'children') : 'loading',
  });

  if (error) {
    return (
      <div className="container flex h-screen w-full items-center justify-center text-red-500">
        {error}
      </div>
    );
  }
  if (!ready) {
    return (
      <>
        <LoadingBar color="#3c92e7" ref={loadingBarRef} />
        <div
          role="status"
          className="flex h-screen items-center justify-center bg-background-sidebar"
        >
          {/* <div
            className="inline-block size-6 animate-spin rounded-full border-[3px] border-current border-t-transparent text-muted-foreground"
            role="status"
            aria-label="loading"
          >
            <span className="sr-only">Loading...</span>
          </div> */}
        </div>
      </>
    );
  }
  if (isOnBoard) {
    return <Onboard />;
  }
  return <>{children}</>;
};

export default ProtectOnBoard;
